import { Link } from "react-router-dom";
import { Card } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { LoadingState, ErrorState, EmptyState } from "@/components/ui/AsyncState";
import { useApi } from "@/lib/use-api";
import { formatRelativeTime } from "@/lib/format";
import { planDisplayName } from "@platform/shared";
import type { AdminCustomerSummary, SubscriptionStatus } from "@platform/shared";

const STATUS_LABEL: Record<SubscriptionStatus, string> = {
  trialing: "Trialing",
  active: "Active",
  past_due: "Past due",
  canceled: "Canceled",
};

const STATUS_TONE: Record<SubscriptionStatus, "neutral" | "signal" | "success"> = {
  trialing: "neutral",
  active: "success",
  past_due: "signal",
  canceled: "neutral",
};

export function Customers() {
  const { data, isLoading, error, refetch } = useApi<{ customers: AdminCustomerSummary[] }>(
    "/api/admin/customers"
  );

  const customers = data?.customers ?? [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl text-ink">Customers</h1>
        <p className="mt-1 text-sm text-ink-soft">Every business using the platform.</p>
      </div>

      {isLoading && <LoadingState label="Loading customers…" />}
      {error && <ErrorState message={error} onRetry={refetch} />}

      {!isLoading && !error && customers.length === 0 && (
        <EmptyState>No customer organizations yet.</EmptyState>
      )}

      {customers.length > 0 && (
        <Card className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-line bg-paper text-ink-soft">
              <tr>
                <th className="px-5 py-2.5 font-medium">Company</th>
                <th className="px-5 py-2.5 font-medium">Owner</th>
                <th className="px-5 py-2.5 font-medium">Status</th>
                <th className="px-5 py-2.5 font-medium">Subscription</th>
                <th className="px-5 py-2.5 font-medium">Created</th>
                <th className="px-5 py-2.5 font-medium">Leads</th>
                <th className="px-5 py-2.5 font-medium">Last activity</th>
                <th className="px-5 py-2.5 font-medium" />
              </tr>
            </thead>
            <tbody>
              {customers.map((customer) => (
                <tr key={customer.organizationId} className="border-b border-line last:border-0 hover:bg-paper">
                  <td className="px-5 py-3 font-medium text-ink">
                    <Link to={`/admin/customers/${customer.organizationId}`} className="hover:underline">
                      {customer.company}
                    </Link>
                  </td>
                  <td className="px-5 py-3 text-ink-soft">
                    {customer.ownerName ?? "—"}
                    {customer.ownerEmail && (
                      <span className="block text-xs text-ink-soft/70">{customer.ownerEmail}</span>
                    )}
                  </td>
                  <td className="px-5 py-3">
                    <Badge tone={customer.isActive ? "success" : "signal"}>
                      {customer.isActive ? "Active" : "Suspended"}
                    </Badge>
                  </td>
                  <td className="px-5 py-3">
                    {customer.subscriptionPlan ? (
                      <div className="flex flex-col gap-1">
                        <span className="text-ink-soft">{planDisplayName(customer.subscriptionPlan)}</span>
                        {customer.subscriptionStatus && (
                          <Badge tone={STATUS_TONE[customer.subscriptionStatus]}>
                            {STATUS_LABEL[customer.subscriptionStatus]}
                          </Badge>
                        )}
                      </div>
                    ) : (
                      <span className="text-ink-soft">—</span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-ink-soft">{formatRelativeTime(customer.createdAt)}</td>
                  <td className="px-5 py-3 text-ink-soft">{customer.leadsCount}</td>
                  <td className="px-5 py-3 text-ink-soft">
                    {customer.lastActivityAt ? formatRelativeTime(customer.lastActivityAt) : "No activity yet"}
                  </td>
                  <td className="px-5 py-3">
                    <Link
                      to={`/admin/customers/${customer.organizationId}`}
                      className="font-medium text-brand-700 hover:underline"
                    >
                      View
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
